import type { OverviewSnapshot } from '@kenocyber/game-domain';
import { formatNumber } from './format';

interface MetricsPanelProps {
  readonly snapshot: OverviewSnapshot | null;
  readonly overviewOpen: boolean;
  readonly onToggleOverview: () => void;
  readonly inventoryOpen: boolean;
  readonly onToggleInventory: () => void;
}

/**
 * Renders a rolling-window metric.
 *
 * The existing rule is preserved exactly: before a full minute has elapsed the
 * accumulated value and the elapsed seconds are shown; afterwards it is the
 * 60-second rolling total.
 */
function MetricValue({
  metric,
  testId,
}: {
  metric: OverviewSnapshot['damageMetric'];
  testId: string;
}) {
  if (metric.elapsedMs === null) {
    return (
      <span className="metric-value" data-testid={testId}>
        —
      </span>
    );
  }
  if (!metric.windowFull) {
    const seconds = Math.floor(metric.elapsedMs / 1000);
    return (
      <span className="metric-value" data-testid={testId}>
        {formatNumber(metric.windowTotal)} <small>em {seconds}s</small>
      </span>
    );
  }
  return (
    <span className="metric-value" data-testid={testId}>
      {formatNumber(metric.windowTotal)}
      <small>/min</small>
    </span>
  );
}

/** Always-visible metrics, floating over the top-right of the arena. */
export function MetricsPanel({
  snapshot,
  overviewOpen,
  onToggleOverview,
  inventoryOpen,
  onToggleInventory,
}: MetricsPanelProps) {
  return (
    <div className="hud hud-right" data-testid="hud-right">
      <dl className="metric-list">
        <div className="metric-row">
          <dt>Dano por tiro</dt>
          <dd className="metric-value" data-testid="metric-damage-per-shot">
            {snapshot ? formatNumber(snapshot.totalDamagePerProjectile) : '—'}
          </dd>
        </div>
        <div className="metric-row">
          <dt>Dano/min</dt>
          <dd>
            {snapshot ? (
              <MetricValue metric={snapshot.damageMetric} testId="metric-dpm" />
            ) : (
              <span className="metric-value" data-testid="metric-dpm">
                —
              </span>
            )}
          </dd>
        </div>
        <div className="metric-row">
          <dt>Ouro/min</dt>
          <dd>
            {snapshot ? (
              <MetricValue metric={snapshot.goldMetric} testId="metric-gpm" />
            ) : (
              <span className="metric-value" data-testid="metric-gpm">
                —
              </span>
            )}
          </dd>
        </div>
        <div className="metric-row">
          <dt>Tiros/segundo</dt>
          <dd className="metric-value" data-testid="metric-shots-per-second">
            {snapshot ? formatNumber(snapshot.totalAttacksPerSecond) : '—'}
          </dd>
        </div>
      </dl>
      <div className="hud-actions">
        <button
          type="button"
          className="overview-toggle"
          data-testid="overview-toggle"
          aria-expanded={overviewOpen}
          onClick={onToggleOverview}
        >
          Overview
        </button>
        <button
          type="button"
          className="overview-toggle"
          data-testid="inventory-toggle"
          aria-expanded={inventoryOpen}
          onClick={onToggleInventory}
        >
          Inventário
        </button>
      </div>
    </div>
  );
}
